/**
 * RefreshTracker — the background-refresh engine, mounted ONCE in the app
 * shell (renders nothing). For every job in refreshJobsStore it owns:
 *
 *  - the SSE subscription (`subscribeJob`, Last-Event-ID replay) folding
 *    frames into the store — progress keeps streaming across navigation;
 *  - live fill: debounced narration/flows artifact refetch on job.node /
 *    job.flow, flushed at the flows phase boundary, so the starter
 *    questions and the guide's context fill in while the job still runs;
 *  - the single sanctioned artifact invalidation on job.done
 *    (invalidateAfterJobDone — the whole repo scope plus the product shapes);
 *  - the success beat: a SUCCEEDED job lingers ~`DONE_LINGER_MS` on the chip
 *    ("Map updated"), then dismisses itself. Failed and cancelled jobs stay
 *    on the chip until dismissed from its popover — an error that fades on
 *    its own is an error nobody saw.
 *
 * It also ADOPTS running jobs the client did not start (reload mid-refresh,
 * another tab): any repo status/detail answer the app fetches that carries
 * an LLM `active_job` gets tracked — the SSE replay rebuilds the fold.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { createDebouncedArtifactInvalidator, invalidateAfterJobDone } from '../../api/queries';
import { subscribeJob } from '../../api/sse';
import type { ActiveJobRef, Ulid } from '../../api/types';
import { useRefreshJobs, type TrackedRefreshJob } from './refreshJobsStore';

/** How long a finished job stays on the chip ("Map updated") before fading. */
export const DONE_LINGER_MS = 6000;

/** The job kinds worth a global chip (LLM-priced, minutes-long). */
const ADOPTABLE_KINDS = new Set(['refresh', 'narrate', 'flows', 'extract']);

function isAdoptable(job: ActiveJobRef | null | undefined): job is ActiveJobRef {
  return (
    job !== null &&
    job !== undefined &&
    ADOPTABLE_KINDS.has(job.kind) &&
    (job.status === 'running' || job.status === 'queued')
  );
}

export function RefreshTracker() {
  const qc = useQueryClient();
  const order = useRefreshJobs((s) => s.order);
  const jobsRef = useRef(new Map<Ulid, () => void>());
  const dismissTimers = useRef(new Map<Ulid, ReturnType<typeof setTimeout>>());

  // One SSE subscription per tracked job (jsdom has no EventSource — the
  // store still folds nothing and the chip simply never appears in tests
  // that do not stub subscribeJob's transport).
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const subs = jobsRef.current;
    const store = useRefreshJobs.getState();

    for (const jobId of order) {
      if (subs.has(jobId)) continue;
      const tracked = store.jobs[jobId];
      if (tracked === undefined) continue;
      const { repoId } = tracked;
      const narration = createDebouncedArtifactInvalidator(qc, repoId, 'narration');
      const flows = createDebouncedArtifactInvalidator(qc, repoId, 'flows');
      const sub = subscribeJob(jobId, {
        onFrame: (frame) => {
          useRefreshJobs.getState().applyFrame(jobId, frame);
          if (frame.type === 'job.node') {
            narration.notify();
          } else if (frame.type === 'job.flow') {
            flows.notify();
          } else if (frame.type === 'job.phase' && frame.data.phase === 'flows') {
            narration.flush();
          } else if (frame.type === 'job.done') {
            void invalidateAfterJobDone(qc, repoId);
          }
        },
        onConnectionError: () => useRefreshJobs.getState().connectionError(jobId),
      });
      subs.set(jobId, () => {
        sub.close();
        narration.dispose();
        flows.dispose();
      });
    }

    // Dismissed/removed jobs release their subscription.
    for (const [jobId, close] of subs) {
      if (!order.includes(jobId)) {
        close();
        subs.delete(jobId);
      }
    }
  }, [order, qc]);

  // The success beat: schedule dismissal once a job folds to done — but ONLY
  // a success. A failed or cancelled job holds the chip until the reader
  // dismisses it from the popover; auto-fading it would erase the one trace
  // that the refresh they paid for did not land. While the user is
  // inspecting the popover the fade waits — never yank the detail out from
  // under them.
  useEffect(() => {
    const timers = dismissTimers.current;
    const schedule = (jobId: Ulid, doneAt: number | null) => {
      if (doneAt === null || timers.has(jobId)) return;
      const delay = Math.max(0, doneAt + DONE_LINGER_MS - Date.now());
      timers.set(
        jobId,
        setTimeout(() => {
          timers.delete(jobId);
          const store = useRefreshJobs.getState();
          if (store.inspecting) {
            // re-arm: try again a beat after the popover closes
            schedule(jobId, Date.now());
            return;
          }
          store.dismiss(jobId);
        }, delay),
      );
    };
    const scheduleSucceeded = (job: TrackedRefreshJob) => {
      if (job.state.done?.status !== 'succeeded') return;
      schedule(job.jobId, job.doneAt);
    };
    for (const job of Object.values(useRefreshJobs.getState().jobs)) {
      scheduleSucceeded(job);
    }
    return useRefreshJobs.subscribe((state) => {
      for (const job of Object.values(state.jobs)) scheduleSucceeded(job);
    });
  }, []);

  // Adoption: watch the query cache for repo status/detail answers carrying
  // a running LLM job (reload mid-refresh, a second tab, the wizard).
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const adoptFrom = (key: unknown, data: unknown) => {
      if (!Array.isArray(key) || typeof key[1] !== 'string') return;
      if (key[0] === 'repo' && (key.length === 2 || (key.length === 3 && key[2] === 'status'))) {
        const active = (data as { active_job?: ActiveJobRef | null } | undefined)?.active_job;
        if (isAdoptable(active)) {
          useRefreshJobs.getState().adopt(key[1], active.id);
        }
        return;
      }
      // Product detail carries every repo's active job too.
      if (key[0] === 'product' && key.length === 2) {
        const repos = (data as { repos?: Array<{ id: string; active_job?: ActiveJobRef | null }> } | undefined)
          ?.repos;
        if (!Array.isArray(repos)) return;
        for (const repo of repos) {
          if (typeof repo?.id === 'string' && isAdoptable(repo.active_job)) {
            useRefreshJobs.getState().adopt(repo.id, repo.active_job.id);
          }
        }
      }
    };
    // Anything already cached (the subscription only sees future updates).
    for (const q of qc.getQueryCache().getAll()) {
      adoptFrom(q.queryKey, q.state.data);
    }
    return qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      adoptFrom(event.query.queryKey, event.query.state.data);
    });
  }, [qc]);

  // Teardown on unmount only.
  useEffect(() => {
    const subs = jobsRef.current;
    const timers = dismissTimers.current;
    return () => {
      for (const close of subs.values()) close();
      subs.clear();
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return null;
}
